import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/styles';
import { IAudioLink } from '../../../dataObjects/models/digitalMedia/AudioLink';
import { enumAudioLinkType, enumDigitalMediaDisplayEnvironment } from '../../../dataObjects/enums';
import { convertGoogleDriveFileUrlForFileLink, urlIsAGoogleDriveLink } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/googleDriveUtilities';
import { MdbError } from '../../../errorObjects/MdbError';
import ReactPlayer from 'react-player';
import { DigitalMediaNotAvailableDisplay } from '../DigitalMediaNotAvailableDisplay/DigitalMediaNotAvailableDisplay';
import { MessagesStringAssets } from '../../../assets/stringAssets';
import { referenceIsForAYouTubeAsset } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/youtubeUtilities';
import { urlIsAMixCloudAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/mixCloudUtilities';
import { urlIsASoundCloudAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/soundCloudUtilities';
import { urlIsAnAppleMusicAddress } from '../../utilities/digitalMediaUtilities/externalSourceUtilities/appleMusicUtilities';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>) that will serve as a base container in a Card View where the audio has NO visual to display
const StyledContainerForCardViewNoVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(0, 0, 2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '10000px', // to give it completely rounded sides
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  justifyContent: 'stretch',
  justifySelf: 'stretch',
  alignContent: 'center',
  alignItems: 'center',
  height: '3rem',
  width: 'auto'
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a Card View where the audio HAS a visual to display
const StyledContainerForCardViewWithVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(0, 0, 2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'stretch',
  minHeight: '250px',
  height: 'auto',
  width: 'auto'
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a Form View where the audio has NO visual to display
const StyledContainerForFormViewNoVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '10000px', // to give it completely rounded sides
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  justifyContent: 'stretch',
  justifySelf: 'stretch',
  alignContent: 'center',
  alignItems: 'center',
  height: '3rem',
  width: 'auto'
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a Form View where the audio provided is unsupported
const StyledContainerForFormViewUnsupported = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  justifyContent: 'stretch',
  justifySelf: 'stretch',
  alignContent: 'center',
  alignItems: 'center',
  height: '3rem',
  width: 'auto'
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a Form View where the audio HAS a visual to display
const StyledContainerForFormViewWithVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(2, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'stretch',
  alignContent: 'stretch',
  minHeight: '350px',
  height: 'auto',
  width: 'auto'
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a List View where the audio has NO visual to display
const StyledContainerForListViewNoVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(1, 2, 1, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '10000px', // to give it completely rounded sides
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  flexGrow: 1,
  position: 'relative',
  width: 'auto',

  [theme.breakpoints.down('xs')]: {
    maxWidth: '300px',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: '350px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '500px',
  },
}));

// a styled Box (equivalent to a <div>) that will serve as a base container in a List View where the audio HAS a visual to display
const StyledContainerForListViewWithVisual = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(1, 2, 1, 0),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '0px', // to give it square corners
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  flexGrow: 1,
  position: 'relative',
  width: 'auto',

  [theme.breakpoints.down('xs')]: {
    maxWidth: '300px',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: '350px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '500px',
  },
}));

// a styled Box (equivalent to a <div>), providing a background for a not available message
const AudioNotAvailableBox = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, #BBB, #EEE, ${theme.palette.primary.light}, #BBB, #EEE, ${theme.palette.primary.light})`,
  display: 'flex',
  justifyContent: 'center',
}));

/**
 * @interface IAudioMediaDisplayProps declares any input properties required for this component.
 */
export interface IAudioMediaDisplayProps extends PropsWithChildren<unknown> {
  audioLink: IAudioLink;
  displayEnvironment: enumDigitalMediaDisplayEnvironment;
}

/**
 * @function AudioMediaDisplay is a React functional component for displaying a simple audio (via an IAudioLink).
 * @param { } props are the properties passed into the component (currently no input properties).
          */
export const AudioMediaDisplay: React.FC<IAudioMediaDisplayProps> = (props: IAudioMediaDisplayProps) => {
  AudioMediaDisplay.displayName = 'Audio Media Display';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  displayConsoleLogs && console.log(`AudioMediaDisplay. Entered AudioMediaDisplay`);

  const { audioLink, displayEnvironment } = props;

  // whether the audio source is supported
  const [supportedAudioSource, setSupportedAudioSource] = useState<boolean>(false);

  // whether the audio has an inherit visual display (is not just audio)
  const [hasInherentVisualDisplay, setHasInherentVisualDisplay] = useState<boolean>(false);

  // the URL that will be set for the audio display object
  const [audioUrl, setAudioUrl] = useState<string>('');

  const audioNotAvailableWidth: string = `100%`;
  const audioNotAvailableHeight: string = `100%`;

  // useEffect to establish container and audio display component parameters based on the audioLink and display environment
  useEffect(() => {
    // The variables that can change are the:
    //   - Whether the Url provided is from a supported audio source
    //   - Url of the audio being displayed
    let localSupportedAudioSource: boolean = false;
    let localAudioUrl: string = audioLink.downloadUrl;
    let localHasInherentVisualDisplay: boolean = false;

    switch (audioLink.audioLinkType) {
      // if the currently selected Audio Type is file upload (GoogleCloudStorage)...
      case enumAudioLinkType.GoogleCloudStorage:
        localSupportedAudioSource = true;
        localHasInherentVisualDisplay = false;
        break;

      // if the currently selected Audio Type is from an external source...
      case enumAudioLinkType.ExternalSource:
        // if the Url is a known supported URL Address where no Url conversion is required: 
        // Apple Music YouTube, MixCloud, SoundCloud...
        if (urlIsAnAppleMusicAddress(audioLink.downloadUrl) ||
          referenceIsForAYouTubeAsset(audioLink.downloadUrl) ||
          urlIsAMixCloudAddress(audioLink.downloadUrl) ||
          urlIsASoundCloudAddress(audioLink.downloadUrl)) {
          localSupportedAudioSource = true;

          localHasInherentVisualDisplay = true;
        } else
          // if the Url is a Google Drive address...
          if (urlIsAGoogleDriveLink(audioLink.downloadUrl)) {
            // not a supported source at this time, so no change to the supported source flag (should be 'false')
            localSupportedAudioSource = false;

            // we'll convert the Url anyway, in case we support in the future
            localAudioUrl = convertGoogleDriveFileUrlForFileLink(audioLink.downloadUrl);
          }

        break;

      default:
        throw new MdbError(`Unhandled audio type (${audioLink.audioLinkType}) in AudioMediaDisplay`);
    }

    if (supportedAudioSource !== localSupportedAudioSource) {
      setSupportedAudioSource(localSupportedAudioSource);
    }

    if (audioUrl !== localAudioUrl) {
      setAudioUrl(localAudioUrl);
    }

    if (hasInherentVisualDisplay !== localHasInherentVisualDisplay) {
      setHasInherentVisualDisplay(localHasInherentVisualDisplay);
    }

  }, [audioLink.audioLinkType, audioLink.downloadUrl]);


  /**
   * @function renderAudioElement Renders the JSX element of the audio object to be displayed on the UI.
   * @param {IAudioLink} audioLink The AudioLink object to be rendered.
                        */
  function renderAudioElement(componentHeight: string): React.JSX.Element {

    // default the audio element to displaying no audio
    let audioElement: React.JSX.Element =
      <>
        <AudioNotAvailableBox
          sx={{ width: audioNotAvailableWidth, height: audioNotAvailableHeight }}
        />
      </>;

    // if there's an established Url for the object, perform logic
    if (audioUrl) {
      // if (supportedSource) {
      if (supportedAudioSource) {
        audioElement =
          <>
            <ReactPlayer
              url={audioUrl}
              crossOrigin="anonymous"
              playsinline
              controls
              minWidth={'100%'}
              width={'100%'}
              minHeight={'100%'}
              height={componentHeight}
            />
          </>
      } else {
        audioElement =
          <>
            <DigitalMediaNotAvailableDisplay displayText={MessagesStringAssets.audioSourceOrFormatNotSupported} />
          </>
      }

    }

    return audioElement;
  }

  /**
   * @function renderAudioContainerAndAudioDisplay Renders the JSX element of the audio element container and audio element to be displayed on the UI.
   */
  function renderAudioContainerAndAudioDisplay(): React.JSX.Element {

    let audioContainerAndAudioDisplay: React.JSX.Element =
      <>
      </>

    displayConsoleLogs && console.log(`AudioMediaDisplay.renderAudioContainerAndAudioDisplay(). displayEnvironment: ${displayEnvironment}`);

    switch (displayEnvironment) {
      case enumDigitalMediaDisplayEnvironment.CardView:
        audioContainerAndAudioDisplay =
          <>
            {/* If the audio object has an inherent visual display, surround it with a container for an inherent visual display */}
            {hasInherentVisualDisplay &&
              <StyledContainerForCardViewWithVisual>
                {renderAudioElement('250px')}
              </StyledContainerForCardViewWithVisual>
            }

            {/* If the audio object has NO inherent visual display, surround it with a container for NO visual display */}
            {!hasInherentVisualDisplay &&
              <StyledContainerForCardViewNoVisual>
                {renderAudioElement('100%')}
              </StyledContainerForCardViewNoVisual>
            }
          </>
        break;

      case enumDigitalMediaDisplayEnvironment.FormView:
        if (!supportedAudioSource) {
          audioContainerAndAudioDisplay =
            <>
              <StyledContainerForFormViewUnsupported>
                {renderAudioElement('100%')}
              </StyledContainerForFormViewUnsupported>
            </>
        } else {
          audioContainerAndAudioDisplay =
            <>
              {/* If the audio object has an inherent visual display, surround it with a container for an inherent visual display */}
              {hasInherentVisualDisplay &&
                <StyledContainerForFormViewWithVisual>
                  {renderAudioElement('350px')}
                </StyledContainerForFormViewWithVisual>
              }

              {/* If the audio object has NO inherent visual display, surround it with a container for NO visual display */}
              {!hasInherentVisualDisplay &&
                <StyledContainerForFormViewNoVisual>
                  {renderAudioElement('100%')}
                </StyledContainerForFormViewNoVisual>
              }
            </>
        }
        break;

      case enumDigitalMediaDisplayEnvironment.ListView:
        audioContainerAndAudioDisplay =
          <>
            {/* If the audio object has an inherent visual display, surround it with a container for an inherent visual display */}
            {
              hasInherentVisualDisplay &&
              <StyledContainerForListViewWithVisual>
                {renderAudioElement('250px')}
              </StyledContainerForListViewWithVisual>
            }

            {/* If the audio object has NO inherent visual display, surround it with a container for NO visual display */}
            {
              !hasInherentVisualDisplay &&
              <StyledContainerForListViewNoVisual>
                {renderAudioElement('100%')}
              </StyledContainerForListViewNoVisual>
            }
          </>
        break;

      default:
        throw new MdbError(`Unhandled display environoment (${displayEnvironment}) in AudioMediaDisplay`);
    }

    return audioContainerAndAudioDisplay;
  }


  return (
    <>
      {renderAudioContainerAndAudioDisplay()}
    </>
  )
}
